import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook to track user inactivity and trigger a callback after timeout
 * @param {Function} onIdle - Callback function when user becomes idle
 * @param {number} timeout - Timeout in milliseconds (default: 15 minutes)
 */
const useIdleTimer = (onIdle, timeout = 15 * 60 * 1000) => {
  const timerRef = useRef(null);
  const onIdleRef = useRef(onIdle);

  // Keep the callback ref updated
  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  const resetTimer = useCallback(() => {
    // Clear existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Set new timer
    timerRef.current = setTimeout(() => {
      onIdleRef.current();
    }, timeout);
  }, [timeout]);

  useEffect(() => {
    // Events to track user activity
    const events = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'wheel'
    ];

    // Throttle function to avoid too many resets
    let lastReset = Date.now();
    const throttledReset = () => {
      const now = Date.now();
      // Only reset if at least 1 second has passed since last reset
      if (now - lastReset >= 1000) {
        lastReset = now;
        resetTimer();
      }
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, throttledReset, { passive: true });
    });

    // Initial timer start
    resetTimer();

    // Cleanup
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      events.forEach(event => {
        document.removeEventListener(event, throttledReset);
      });
    };
  }, [resetTimer]);

  return { resetTimer };
};

export default useIdleTimer;
